<template>
    <div class="notifications-tab">
        <v-container fluid>
            <v-alert
                v-if="error"
                type="error"
                dismissible
                @input="error = null"
            >
                {{ error }}
            </v-alert>

            <fieldset class="talentics-fieldset talentics-fieldset-paddings">
                <legend>{{ $t('label.notification-settings') }}</legend>
                
                <div class="toggle-section">
                    <v-switch
                        v-model="settings.is_active"
                        :label="$t('label.enable-hiring-plan-notifications')"
                        @change="handleSettingsChange"
                    ></v-switch>
                </div>

                <v-expand-transition>
                    <div v-if="settings.is_active" class="notifications-content">
                        <v-card outlined>
                            <v-card-title class="notification-title">
                                <span>{{ $t('label.notification-intervals') }}</span>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon
                                            small
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            mdi-help-circle-outline
                                        </v-icon>
                                    </template>
                                    <span>{{ $t('tooltip.notification-intervals-help') }}</span>
                                </v-tooltip>
                            </v-card-title>

                            <v-card-text class="notification-content">
                                <div class="preset-section">
                                    <div class="section-title">{{ $t('label.quick-presets') }}</div>
                                    <div class="preset-chips">
                                        <v-chip
                                            v-for="preset in presetIntervals"
                                            :key="preset.value"
                                            :color="isIntervalSelected(preset.value) ? 'primary' : ''"
                                            :class="{'preset-selected': isIntervalSelected(preset.value)}"
                                            outlined
                                            @click="togglePresetInterval(preset.value)"
                                        >
                                            {{ preset.label }}
                                        </v-chip>
                                    </div>
                                </div>

                                <div class="custom-interval-section">
                                    <div class="section-title">{{ $t('label.custom-interval') }}</div>
                                    <div class="interval-input-container">
                                        <v-text-field
                                            v-model.number="newInterval"
                                            type="number"
                                            :placeholder="$t('label.enter-days')"
                                            :hint="$t('label.days-before-after')"
                                            persistent-hint
                                            outlined
                                            dense
                                            class="interval-input"
                                            :rules="[v => !v || (v >= -30 && v <= 30) || $t('err.interval-range')]"
                                            @keyup.enter="addInterval"
                                        ></v-text-field>
                                        <v-btn
                                            color="primary"
                                            @click="addInterval"
                                            :disabled="!isValidInterval"
                                            :loading="loading"
                                            class="add-interval-btn"
                                        >
                                            {{ $t('button.add-interval') }}
                                        </v-btn>
                                    </div>
                                </div>

                                <div v-if="settings.notify_interval.length > 0" class="timeline-section">
                                    <div class="section-title">{{ $t('label.selected-notification-times') }}</div>
                                    <div class="timeline-container">
                                        <div class="timeline-line"></div>
                                        <div class="timeline-points">
                                            <div 
                                                v-for="point in timelinePoints" 
                                                :key="point.key"
                                                class="timeline-point-wrapper"
                                            >
                                                <div class="timeline-point" :class="point.color">
                                                    <button 
                                                        v-if="point.type !== 'deadline'"
                                                        class="remove-button"
                                                        @click="removeInterval(point.originalValue)"
                                                        aria-label="Remove interval"
                                                    >
                                                        <v-icon x-small>mdi-close</v-icon>
                                                    </button>
                                                </div>
                                                <div class="timeline-label">{{ point.label }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else class="empty-state">
                                    <v-icon large color="grey lighten-1">mdi-bell-off-outline</v-icon>
                                    <div class="empty-state-text">
                                        {{ $t('text.no-intervals-set') }}
                                    </div>
                                </div>
                                
                                <div class="interval-help-section">
                                    <v-card outlined>
                                        <div class="interval-help-section-title">{{ $t('text.how-intervals-work') }}</div>
                                        <div class="interval-help-content">
                                            <div class="interval-help-item">
                                                <div class="interval-help-icon success">
                                                    <v-icon color="white" small>mdi-plus-circle</v-icon>
                                                </div>
                                                <div class="interval-help-text">
                                                    <div class="interval-help-item-title">{{ $t('text.positive-interval-example', { days: 7 }) }}</div>
                                                    <div class="interval-help-description">{{ $t('text.positive-interval-explanation') }}</div>
                                                </div>
                                            </div>
                                            <div class="interval-help-item">
                                                <div class="interval-help-icon error">
                                                    <v-icon color="white" small>mdi-minus-circle</v-icon>
                                                </div>
                                                <div class="interval-help-text">
                                                    <div class="interval-help-item-title">{{ $t('text.negative-interval-example', { days: 3 }) }}</div>
                                                    <div class="interval-help-description">{{ $t('text.negative-interval-explanation') }}</div>
                                                </div>
                                            </div>
                                            <div class="interval-help-item">
                                                <div class="interval-help-icon warning">
                                                    <v-icon color="white" small>mdi-clock</v-icon>
                                                </div>
                                                <div class="interval-help-text">
                                                    <div class="interval-help-item-title">{{ $t('text.zero-interval-example') }}</div>
                                                    <div class="interval-help-description">{{ $t('text.zero-interval-explanation') }}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </v-card>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>
                </v-expand-transition>
            </fieldset>
        </v-container>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'HiringPlanNotificationsTab',
    
    data() {
        return {
            settings: {
                is_active: false,
                notify_interval: []
            },
            newInterval: null,
            loading: false,
            error: null,
            showLeftIndicator: false,
            showRightIndicator: false,
            presetIntervals: [
                { value: 7, label: this.$t('text.one-week-before') },
                { value: 3, label: this.$t('text.three-days-before') },
                { value: 1, label: this.$t('text.one-day-before') },
                { value: 0, label: this.$t('text.on-deadline-day') },
                { value: -1, label: this.$t('text.one-day-after') },
                { value: -3, label: this.$t('text.three-days-after') }
            ]
        };
    },

    computed: {
        ...mapState({
            isDark: state => state.currentUser.isDark
        }),

        isValidInterval() {
            const intervals = this.settings?.notify_interval || [];
            return this.newInterval !== null && 
                   !isNaN(this.newInterval) && 
                   this.newInterval >= -30 && 
                   this.newInterval <= 30 &&
                   !intervals.includes(Number(this.newInterval)) &&
                   intervals.length < 6;
        },

        sortedIntervals() {
            const intervals = this.settings?.notify_interval || [];
            return [...intervals].sort((a, b) => b - a);
        },

        deadlineDate() {
            const today = new Date();
            return today.toLocaleDateString();
        },

        beforeDeadlinePoints() {
            return this.sortedIntervals.filter(interval => interval > 0);
        },

        afterDeadlinePoints() {
            return this.sortedIntervals.filter(interval => interval <= 0);
        },

        timelinePoints() {
            const points = [];
            const intervals = this.sortedIntervals;

            for (let i = 0; i < intervals.length; i++) {
                const interval = intervals[i];
                
                points.push({
                    key: `point-${interval}`,
                    type: 'interval',
                    color: interval > 0 ? 'success' : 'error',
                    label: this.formatInterval(interval),
                    originalValue: interval
                });

                if (this.shouldShowDeadlineAfterIndex(i)) {
                    points.push({
                        key: `deadline-${interval}`,
                        type: 'deadline',
                        color: 'warning',
                        label: this.deadlineDate + ' ' + this.$t('text.deadline'),
                        originalValue: interval
                    });
                }
            }

            return points;
        }
    },

    methods: {
        async fetchSettings() {
            this.loading = true;
            this.error = null;
            try {
                const response = await this.$store.dispatch('hiringPlanNotifications/getSettings');
                const intervals = response.data?.notify_interval || [];
                this.settings = {
                    is_active: response.data?.is_active || false,
                    notify_interval: Array.isArray(intervals) ? intervals : [intervals]
                };
            } catch (error) {
                console.error('Error fetching hiring plan notification settings:', error);
                this.error = this.$t('err.an-error-occurred');
                this.settings = {
                    is_active: false,
                    notify_interval: []
                };
            } finally {
                this.loading = false;
            }
        },

        async handleSettingsChange() {
            this.loading = true;
            this.error = null;
            try {
                await this.$store.dispatch('hiringPlanNotifications/updateSettings', this.settings);
            } catch (error) {
                console.error('Error updating hiring plan notification settings:', error);
                this.error = this.$t('err.an-error-occurred');
                this.settings.is_active = !this.settings.is_active;
            } finally {
                this.loading = false;
            }
        },

        formatInterval(days) {
            if (days > 0) {
                return this.$t('text.days-before-deadline', { days });
            } else if (days < 0) {
                return this.$t('text.days-after-deadline', { days: Math.abs(days) });
            }
            return this.$t('text.on-deadline-day');
        },

        getIntervalColor(days) {
            if (days > 0) return 'success';
            if (days < 0) return 'error';
            return 'warning';
        },

        isIntervalSelected(interval) {
            return this.settings?.notify_interval?.includes(interval) || false;
        },

        async togglePresetInterval(interval) {
            if (this.isIntervalSelected(interval)) {
                await this.removeInterval(interval);
            } else {
                this.newInterval = interval;
                await this.addInterval();
            }
        },

        async addInterval() {
            const intervals = this.settings?.notify_interval || [];
            if (intervals.length >= 6) {
                this.error = this.$t('err.max-intervals-reached');
                return;
            }
            if (!this.isValidInterval) return;
            const newInterval = Number(this.newInterval);
            const updatedIntervals = [...intervals, newInterval].sort((a, b) => b - a);
            this.loading = true;
            this.error = null;
            try {
                await this.$store.dispatch('hiringPlanNotifications/updateSettings', {
                    ...this.settings,
                    notify_interval: updatedIntervals
                });
                this.settings.notify_interval = updatedIntervals;
                this.newInterval = null;
            } catch (error) {
                console.error('Error adding interval:', error);
                this.error = this.$t('err.an-error-occurred');
            } finally {
                this.loading = false;
            }
        },

        async removeInterval(interval) {
            const updatedIntervals = this.settings.notify_interval.filter(i => i !== interval);
            
            this.loading = true;
            this.error = null;
            try {
                await this.$store.dispatch('hiringPlanNotifications/updateSettings', {
                    ...this.settings,
                    notify_interval: updatedIntervals
                });
                this.settings.notify_interval = updatedIntervals;
            } catch (error) {
                console.error('Error removing interval:', error);
                this.error = this.$t('err.an-error-occurred');
            } finally {
                this.loading = false;
            }
        },

        getPointClass(interval) {
            return interval > 0 ? 'success' : 'error';
        },

        getPointPosition(interval) {
            const maxInterval = 30;
            const percentage = ((interval + maxInterval) / (maxInterval * 2)) * 100;
            return {
                left: `${percentage}%`,
                top: interval > 0 ? '0' : '100%'
            };
        },

        shouldShowDeadlineAfterIndex(index) {
            const intervals = this.sortedIntervals;
            if (index >= intervals.length - 1) return false;
            
            const currentInterval = intervals[index];
            const nextInterval = intervals[index + 1];
            
            return currentInterval > 0 && nextInterval <= 0;
        },

        checkScroll() {
            const wrapper = this.$refs.timelineWrapper;
            if (wrapper) {
                this.showLeftIndicator = wrapper.scrollLeft > 0;
                this.showRightIndicator = wrapper.scrollLeft < (wrapper.scrollWidth - wrapper.clientWidth);
            }
        },

        scrollRight() {
            const wrapper = this.$refs.timelineWrapper;
            if (wrapper) {
                wrapper.scrollBy({
                    left: 200,
                    behavior: 'smooth'
                });
            }
        },

        scrollLeft() {
            const wrapper = this.$refs.timelineWrapper;
            if (wrapper) {
                wrapper.scrollBy({
                    left: -200,
                    behavior: 'smooth'
                });
            }
        }
    },

    created() {
        this.fetchSettings();
    },

    mounted() {
        this.checkScroll();
        window.addEventListener('resize', this.checkScroll);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkScroll);
    }
};
</script>

<style scoped>
.notifications-tab {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.toggle-section {
    display: flex;
    margin-bottom: 16px;
}

.notifications-content {
    display: flex;
    flex-direction: column;
}

.notification-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.notification-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.section-title {
    font-weight: 500;
    font-size: 0.875rem;
    margin-bottom: 12px;
}

.preset-section {
    display: flex;
    flex-direction: column;
}

.preset-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.preset-selected {
    font-weight: 500;
}

.custom-interval-section {
    display: flex;
    flex-direction: column;
}

.interval-input-container {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.interval-input {
    max-width: 200px;
    flex-grow: 0;
    flex-shrink: 0;
}

.add-interval-btn {
    align-self: flex-start;
}

/* Timeline section */
.timeline-section {
    display: flex;
    flex-direction: column;

    width: 100%;
    flex-grow: 0;
    flex-shrink: 1;
    overflow: hidden;

    margin-top: 8px;
}

.timeline-container {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    flex-grow: 0;
    flex-shrink: 1;
    position: relative;
    overflow: hidden;
    
    width: 100%;
    overflow-x: auto;
    padding: 20px 0;
    background-color: var(--v-background-base, #f5f5f5);
    border-radius: 4px;
}

.timeline-line {
    position: absolute;
    left: 20px;
    right: 20px;
    top: 47.5px;
    height: 2px;
    background-color: rgba(0, 0, 0, 0.12);
}

.timeline-points {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: center;
    flex-grow: 0;
    flex-shrink: 1;
    overflow-x: auto;

    padding: 20px 20px 0;
    gap: 1.5rem;
    box-sizing: border-box;
    min-width: max-content;
    position: relative;
    z-index: 1;
}

.timeline-point-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    flex: 0 0 120px;
    position: relative;
}

.timeline-point {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.timeline-point.success {
    background-color: var(--v-success-base, #4CAF50);
}

.timeline-point.error {
    background-color: var(--v-error-base, #FF5252);
}

.timeline-point.warning {
    background-color: var(--v-warning-base, #FFC107);
}

.remove-button {
    position: absolute;
    top: -8px;
    right: -8px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
}

.timeline-point-wrapper:hover .remove-button {
    opacity: 1;
}

.timeline-label {
    font-size: 0.875rem;
    text-align: center;
    width: 100%;
    word-wrap: break-word;
}

/* Empty state */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.empty-state-text {
    margin-top: 8px;
    color: var(--v-medium-emphasis-base, rgba(0, 0, 0, 0.6));
}

/* How intervals work section */
.interval-help-section {
    margin-top: 24px;
    padding: 1rem;
}

.interval-help-section-title {
    font-weight: 500;
    font-size: 0.875rem;
    padding: 16px 16px 8px;
}

.interval-help-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 16px 16px;
}

.interval-help-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.interval-help-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.interval-help-icon.success {
    background-color: var(--v-success-base, #4CAF50);
}

.interval-help-icon.error {
    background-color: var(--v-error-base, #FF5252);
}

.interval-help-icon.warning {
    background-color: var(--v-warning-base, #FFC107);
}

.interval-help-text {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
}

.interval-help-item-title {
    font-weight: 500;
    font-size: 0.875rem;
}

.interval-help-description {
    font-size: 0.75rem;
    color: var(--v-medium-emphasis-base, rgba(0, 0, 0, 0.6));
}

/* Dark theme adjustments */
:deep(.theme--dark) .timeline-container {
    background-color: var(--v-background-darken-1, #1E1E1E);
}

:deep(.theme--dark) .timeline-line {
    background-color: rgba(255, 255, 255, 0.12);
}

:deep(.theme--dark) .empty-state-text {
    color: var(--v-medium-emphasis-base, rgba(255, 255, 255, 0.6));
}

:deep(.theme--dark) .interval-help-description {
    color: var(--v-medium-emphasis-base, rgba(255, 255, 255, 0.6));
}
</style> 